/** 画布表格行列拖拽：由 layout 尺寸换算屏幕坐标（不依赖 table DOM 行节点） */

export type DragBox = {
  left: number;
  top: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
};

const DROP_LINE_ROW_THICKNESS_PX = 2;
const DROP_LINE_COL_THICKNESS_PX = 2;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function intervalsOverlap1D(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 < b1 && a1 > b0;
}

export function rowScreenBox(
  tableRect: DOMRect,
  rowHeights: number[],
  row: number,
  layoutHeight: number,
): DragBox {
  const before = rowHeights.slice(0, row).reduce((a, b) => a + b, 0);
  const top = tableRect.top + (before / layoutHeight) * tableRect.height;
  const height = (rowHeights[row] / layoutHeight) * tableRect.height;
  return {
    left: tableRect.left,
    top,
    width: tableRect.width,
    height,
    bottom: top + height,
    right: tableRect.right,
  };
}

export function colScreenBox(
  tableRect: DOMRect,
  colWidths: number[],
  col: number,
  layoutWidth: number,
): DragBox {
  const before = colWidths.slice(0, col).reduce((a, b) => a + b, 0);
  const left = tableRect.left + (before / layoutWidth) * tableRect.width;
  const width = (colWidths[col] / layoutWidth) * tableRect.width;
  return {
    left,
    top: tableRect.top,
    width,
    height: tableRect.height,
    bottom: tableRect.bottom,
    right: left + width,
  };
}

export function rowMoveToIndex(from: number, insertSlot: number, rowCount: number): number {
  let to = insertSlot;
  if (from < insertSlot) to = insertSlot - 1;
  return clamp(to, 0, rowCount - 1);
}

export function colMoveToIndex(from: number, insertSlot: number, colCount: number): number {
  let to = insertSlot;
  if (from < insertSlot) to = insertSlot - 1;
  return clamp(to, 0, colCount - 1);
}

export function resolveCanvasRowLineDrag(
  fromIndex: number,
  offsetY: number,
  rowBox: DragBox,
  tableRect: DOMRect,
  rowHeights: number[],
  layoutHeight: number,
): { dropLine: { top: number; left: number; width: number; height: number }; insertSlot: number } | null {
  const rowCount = rowHeights.length;
  if (rowCount <= 0 || fromIndex < 0 || fromIndex >= rowCount) return null;

  const ghostTop = rowBox.top + offsetY;
  const ghostBottom = ghostTop + rowBox.height;

  const overlapping: number[] = [];
  for (let k = 0; k < rowCount; k++) {
    const r = rowScreenBox(tableRect, rowHeights, k, layoutHeight);
    if (intervalsOverlap1D(ghostTop, ghostBottom, r.top, r.bottom)) overlapping.push(k);
  }

  const downs = overlapping.filter((k) => k > fromIndex);
  const jDown = downs.length > 0 ? Math.max(...downs) : -1;
  const ups = overlapping.filter((k) => k < fromIndex);
  const jUp = ups.length > 0 ? Math.min(...ups) : -1;

  const h = DROP_LINE_ROW_THICKNESS_PX;
  let insertSlot: number;
  let lineCenterY: number;

  if (offsetY > 0) {
    if (jDown < 0) return null;
    const r = rowScreenBox(tableRect, rowHeights, jDown, layoutHeight);
    insertSlot = jDown + 1;
    lineCenterY = r.bottom;
  } else if (offsetY < 0) {
    if (jUp < 0) return null;
    const r = rowScreenBox(tableRect, rowHeights, jUp, layoutHeight);
    insertSlot = jUp;
    lineCenterY = r.top;
  } else {
    if (jDown >= 0) {
      const r = rowScreenBox(tableRect, rowHeights, jDown, layoutHeight);
      insertSlot = jDown + 1;
      lineCenterY = r.bottom;
    } else if (jUp >= 0) {
      const r = rowScreenBox(tableRect, rowHeights, jUp, layoutHeight);
      insertSlot = jUp;
      lineCenterY = r.top;
    } else {
      return null;
    }
  }

  if (rowMoveToIndex(fromIndex, insertSlot, rowCount) === fromIndex) return null;
  return {
    insertSlot,
    dropLine: {
      top: lineCenterY - h / 2,
      left: tableRect.left,
      width: tableRect.width,
      height: h,
    },
  };
}

export function resolveCanvasColLineDrag(
  fromIndex: number,
  offsetX: number,
  colBox: DragBox,
  tableRect: DOMRect,
  colWidths: number[],
  layoutWidth: number,
): { dropLine: { left: number; top: number; width: number; height: number }; insertSlot: number } | null {
  const colCount = colWidths.length;
  if (colCount <= 0 || fromIndex < 0 || fromIndex >= colCount) return null;

  const ghostLeft = colBox.left + offsetX;
  const ghostRight = ghostLeft + colBox.width;

  const overlapping: number[] = [];
  for (let k = 0; k < colCount; k++) {
    const c = colScreenBox(tableRect, colWidths, k, layoutWidth);
    if (intervalsOverlap1D(ghostLeft, ghostRight, c.left, c.right)) overlapping.push(k);
  }

  const rights = overlapping.filter((k) => k > fromIndex);
  const jRight = rights.length > 0 ? Math.max(...rights) : -1;
  const lefts = overlapping.filter((k) => k < fromIndex);
  const jLeft = lefts.length > 0 ? Math.min(...lefts) : -1;

  const w = DROP_LINE_COL_THICKNESS_PX;
  let insertSlot: number;
  let lineCenterX: number;

  if (offsetX > 0) {
    if (jRight < 0) return null;
    const c = colScreenBox(tableRect, colWidths, jRight, layoutWidth);
    insertSlot = jRight + 1;
    lineCenterX = c.right;
  } else if (offsetX < 0) {
    if (jLeft < 0) return null;
    const c = colScreenBox(tableRect, colWidths, jLeft, layoutWidth);
    insertSlot = jLeft;
    lineCenterX = c.left;
  } else {
    if (jRight >= 0) {
      const c = colScreenBox(tableRect, colWidths, jRight, layoutWidth);
      insertSlot = jRight + 1;
      lineCenterX = c.right;
    } else if (jLeft >= 0) {
      const c = colScreenBox(tableRect, colWidths, jLeft, layoutWidth);
      insertSlot = jLeft;
      lineCenterX = c.left;
    } else {
      return null;
    }
  }

  if (colMoveToIndex(fromIndex, insertSlot, colCount) === fromIndex) return null;
  return {
    insertSlot,
    dropLine: {
      left: lineCenterX - w / 2,
      top: tableRect.top,
      width: w,
      height: tableRect.height,
    },
  };
}
